import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

/**
 * Hook to check admin/manager status using server-side verification.
 * Both admin and manager roles are verified through the verify-admin Edge Function
 * to ensure defense-in-depth (no client-side role queries).
 */
export const useAdminStatus = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkAdminStatus = useCallback(async () => {
    if (!user) {
      setIsAdmin(false);
      setIsManager(false);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('verify-admin');
      
      if (error) {
        if (import.meta.env.DEV) {
          console.error('Error verifying role status:', error);
        }
        setIsAdmin(false);
        setIsManager(false);
      } else {
        setIsAdmin(data?.isAdmin === true);
        setIsManager(data?.isManager === true);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error in role status check:', error);
      }
      setIsAdmin(false);
      setIsManager(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading) {
      checkAdminStatus();
    }
  }, [authLoading, checkAdminStatus]);

  return {
    isAdmin,
    isManager,
    isAdminOrManager: isAdmin || isManager,
    loading: authLoading || loading,
    refetch: checkAdminStatus,
  };
};
